import { Component, DestroyRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap, timer } from 'rxjs';
import { MenuStateService } from '../../menu-state.service';
import { ContactRelationshipService } from './relationship.service';
import { ContactChoice, GroupChoice, PreferenceInput, PreferenceRow, RelationshipCatalogue, RelationshipDetail, RelationshipRow, RelationshipSave, RelationshipType } from './relationship.models';
import { RoutingStopsComponent } from './routing-stops/routing-stops.component';
type ContactRole = 'source' | 'target';
@Component({
    selector: 'app-contact-relationship-manager', templateUrl: './contact-relationship-manager.component.html',
    styleUrls: ['./contact-relationship-manager.component.scss']
})
export class ContactRelationshipManagerComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    @ViewChild(RoutingStopsComponent)
    routingStops?: RoutingStopsComponent;
    activeTab: 'relationships' | 'routing' = 'relationships';
    catalogue: RelationshipCatalogue | null = null;
    rows: RelationshipRow[] = [];
    selected: RelationshipRow | null = null;
    preferences: PreferenceRow[] = [];
    loading = false;
    detailLoading = false;
    saving = false;
    dark = false;
    error = '';
    notice = '';
    search = '';
    filterType = '';
    customerFilter = '';
    offset = 0;
    total = 0;
    type = '';
    source: ContactChoice | null = null;
    target: ContactChoice | null = null;
    notes = '';
    sourceQuery = '';
    targetQuery = '';
    contactResults: Record<ContactRole, ContactChoice[]> = { source: [], target: [] };
    contactBusy: Record<ContactRole, boolean> = { source: false, target: false };
    group: GroupChoice | null = null;
    groupQuery = '';
    groupResults: GroupChoice[] = [];
    groupBusy = false;
    preference: PreferenceInput = this.blankPreference();
    editingPreference: PreferenceRow | null = null;
    relationshipDirty = false;
    preferenceDirty = false;
    private listRequest = 0;
    private detailRequest = 0;
    private readonly contactSearches: Record<ContactRole, Subject<string>> = { source: new Subject(), target: new Subject() };
    private readonly groupSearches = new Subject<string>();
    constructor(private readonly api: ContactRelationshipService, private readonly route: ActivatedRoute, readonly theme: MenuStateService) { }
    get definition(): RelationshipType | undefined { return this.catalogue?.types.find(type => type.relationship_type === this.type); }
    get identityLocked(): boolean { return this.preferences.length > 0; }
    get canSave(): boolean {
        return !this.saving && !this.detailLoading && !!this.definition && !!this.target && (!this.definition.requires_source || !!this.source)
            && !!(this.selected ? this.catalogue?.capabilities.edit : this.catalogue?.capabilities.add);
    }
    get hasMore(): boolean { return this.offset + this.rows.length < this.total; }
    private blankPreference(): PreferenceInput { return { group_uuid: null, group_kind: null, movement_role: '', priority: 100, active: true, effective_from: '', effective_to: '', notes: '' }; }
    ngOnInit(): void {
        this.theme.themeState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => this.dark = value === 'ams-content-dark');
        this.watchContacts('source');
        this.watchContacts('target');
        this.watchGroups();
        this.api.catalogue().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: catalogue => {
                this.catalogue = catalogue;
                const params = this.route.snapshot.queryParamMap;
                const type = params.get('type') || '';
                if (catalogue.types.some(item => item.relationship_type === type)) {
                    this.filterType = type;
                    this.type = type;
                }
                this.customerFilter = params.get('customer') || '';
                this.newPreference(false);
                this.load();
                if (this.customerFilter && this.definition?.requires_source) {
                    this.api.contacts(this.type, 'source', '', this.customerFilter).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                        next: result => { if (!this.relationshipDirty && !this.selected)
                            this.source = result.data[0] || null; }, error: error => this.failed(error)
                    });
                }
            }, error: error => this.failed(error)
        });
    }
    private failed(error: {
        error?: {
            message?: string;
        };
    }): void { this.saving = false; this.error = error.error?.message || 'The request could not be completed.'; }
    load(offset = this.offset): void {
        const request = ++this.listRequest;
        this.loading = true;
        this.error = '';
        this.offset = offset;
        this.api.list({ type: this.filterType, customer: this.customerFilter, q: this.search, offset: String(offset) })
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: result => { if (request !== this.listRequest)
                return; this.rows = result.data; this.total = result.total; this.loading = false; },
            error: error => { if (request !== this.listRequest)
                return; this.loading = false; this.rows = []; this.failed(error); }
        });
    }
    changed(): void { this.relationshipDirty = true; this.notice = ''; }
    ruleChanged(): void { this.preferenceDirty = true; this.notice = ''; }
    private confirmDiscard(): boolean { return !(this.relationshipDirty || this.preferenceDirty) || window.confirm('Discard the unsaved relationship or preference changes?'); }
    newRelationship(): void {
        if (this.saving)
            return;
        if (!this.confirmDiscard())
            return;
        this.detailRequest++;
        this.detailLoading = false;
        this.selected = null;
        this.preferences = [];
        this.source = null;
        this.target = null;
        this.notes = '';
        this.type = this.filterType;
        this.clearSearches();
        this.relationshipDirty = false;
        this.newPreference(false);
        this.notice = '';
    }
    changeType(): void {
        this.source = null;
        this.target = null;
        this.clearSearches();
        this.changed();
    }
    private clearSearches(): void {
        this.sourceQuery = '';
        this.targetQuery = '';
        this.contactResults = { source: [], target: [] };
        this.contactSearches.source.next('');
        this.contactSearches.target.next('');
        this.groupSearches.next('');
    }
    searchContact(role: ContactRole, q: string): void {
        if (role === 'source')
            this.sourceQuery = q;
        else
            this.targetQuery = q;
        this.contactResults[role] = [];
        this.contactBusy[role] = q.length >= 2;
        this.contactSearches[role].next(q);
    }
    private watchContacts(role: ContactRole): void {
        this.contactSearches[role].pipe(switchMap(q => {
            if (q.length < 2 || !this.type)
                return of({ data: [] as ContactChoice[] });
            return timer(250).pipe(switchMap(() => this.api.contacts(this.type, role, q)), catchError(error => { this.failed(error); return of({ data: [] as ContactChoice[] }); }));
        }), takeUntilDestroyed(this.destroyRef)).subscribe(result => { this.contactBusy[role] = false; this.contactResults[role] = result.data; });
    }
    chooseContact(role: ContactRole, contact: ContactChoice | null): void {
        if (role === 'source') {
            this.source = contact;
            this.sourceQuery = '';
            this.group = null;
            this.groupSearches.next('');
        }
        else {
            this.target = contact;
            this.targetQuery = '';
        }
        this.contactResults[role] = [];
        this.contactSearches[role].next('');
        this.changed();
    }
    edit(row: RelationshipRow): void {
        if (this.saving)
            return;
        if (!this.confirmDiscard())
            return;
        const request = ++this.detailRequest;
        this.error = '';
        this.detailLoading = true;
        this.api.detail(row.contact_relationship_uuid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: detail => { if (request === this.detailRequest) {
                this.detailLoading = false;
                this.adopt(detail);
            } }, error: error => { if (request === this.detailRequest) {
                this.detailLoading = false;
                this.failed(error);
            } }
        });
    }
    private adopt(detail: RelationshipDetail): void {
        this.selected = detail.data;
        this.type = detail.data.relationship_type;
        this.notes = detail.data.notes;
        this.preferences = detail.preferences;
        this.source = detail.data.source_contact_uuid ? { contact_uuid: detail.data.source_contact_uuid, display_name: detail.data.source_name, active: null } : null;
        this.target = { contact_uuid: detail.data.target_contact_uuid, display_name: detail.data.target_name, active: null };
        this.relationshipDirty = false;
        this.clearSearches();
        this.newPreference(false);
    }
    saveRelationship(): void {
        if (!this.canSave)
            return;
        if (this.preferenceDirty && !window.confirm('Saving the relationship will discard the unsaved preference form. Continue?'))
            return;
        const body: RelationshipSave = { relationship_type: this.type, source_contact_uuid: this.definition!.requires_source ? this.source!.contact_uuid : null,
            target_contact_uuid: this.target!.contact_uuid, notes: this.notes };
        if (this.selected)
            body.expected_version = this.selected.version;
        this.saving = true;
        this.error = '';
        this.api.save(body, this.selected?.contact_relationship_uuid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: detail => { this.saving = false; this.adopt(detail); this.notice = detail.data.supports_preferences ? 'Relationship saved. Add carrier preferences below.' : 'Relationship saved.'; this.load(); },
            error: error => this.failed(error)
        });
    }
    removeRelationship(): void {
        if (!this.selected || !this.catalogue?.capabilities.delete || this.saving || this.detailLoading)
            return;
        if (!window.confirm('Remove this relationship and all of its preference rules? The change is recorded in history.'))
            return;
        this.saving = true;
        this.error = '';
        this.api.remove(this.selected.contact_relationship_uuid, this.selected.version).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => { this.saving = false; this.relationshipDirty = false; this.preferenceDirty = false; this.newRelationship(); this.notice = 'Relationship removed.'; this.load(0); },
            error: error => this.failed(error)
        });
    }
    newPreference(confirm = true): void {
        if (confirm && this.preferenceDirty && !window.confirm('Discard the unsaved preference changes?'))
            return;
        this.editingPreference = null;
        this.preference = this.blankPreference();
        this.group = null;
        this.groupQuery = '';
        this.groupResults = [];
        this.preference.movement_role = this.catalogue?.movement_roles[0]?.movement_role || '';
        this.groupSearches.next('');
        this.preferenceDirty = false;
    }
    editPreference(row: PreferenceRow): void {
        if (this.preferenceDirty && !window.confirm('Discard the unsaved preference changes?'))
            return;
        this.editingPreference = row;
        this.preference = { group_uuid: row.group_uuid, group_kind: row.group_kind, movement_role: row.movement_role,
            priority: row.priority, active: row.active, effective_from: row.effective_from || '', effective_to: row.effective_to || '', notes: row.notes };
        this.group = row.group;
        this.groupQuery = '';
        this.groupResults = [];
        this.groupSearches.next('');
        this.preferenceDirty = false;
    }
    changeGroupKind(): void { this.group = null; this.preference.group_uuid = null; this.groupQuery = ''; this.groupResults = []; this.groupSearches.next(''); this.ruleChanged(); }
    searchGroup(q: string): void { this.groupQuery = q; this.groupResults = []; this.groupBusy = q.length >= 2; this.groupSearches.next(q); }
    private watchGroups(): void {
        this.groupSearches.pipe(switchMap(q => {
            if (q.length < 2 || !this.source || !this.preference.group_kind)
                return of({ data: [] as GroupChoice[] });
            return timer(250).pipe(switchMap(() => this.api.groups(this.type, this.source!.contact_uuid, this.preference.group_kind!, q)), catchError(error => { this.failed(error); return of({ data: [] as GroupChoice[] }); }));
        }), takeUntilDestroyed(this.destroyRef)).subscribe(result => { this.groupBusy = false; this.groupResults = result.data; });
    }
    chooseGroup(group: GroupChoice | null): void {
        this.group = group;
        this.preference.group_uuid = group?.group_uuid || null;
        this.groupQuery = '';
        this.groupResults = [];
        this.groupSearches.next('');
        this.ruleChanged();
    }
    savePreference(): void {
        if (!this.selected || !this.catalogue?.capabilities.edit || this.saving || this.detailLoading || this.relationshipDirty || !this.preference.movement_role)
            return;
        if (this.preference.group_kind && !this.group) {
            this.error = 'Select a maintained group from Pool Manager.';
            return;
        }
        const body = { ...this.preference, group_uuid: this.group?.group_uuid || null,
            group_kind: this.group?.group_kind || null, ...(this.editingPreference ? { expected_version: this.editingPreference.version } : {}) };
        this.saving = true;
        this.error = '';
        this.api.savePreference(this.selected.contact_relationship_uuid, body, this.editingPreference?.carrier_preference_uuid)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: detail => { this.saving = false; this.adopt(detail); this.notice = 'Carrier preference saved.'; this.load(); }, error: error => this.failed(error)
        });
    }
    removePreference(row: PreferenceRow): void {
        if (!this.selected || !this.catalogue?.capabilities.delete || this.saving || this.detailLoading || this.relationshipDirty || this.preferenceDirty)
            return;
        if (!window.confirm('Remove this carrier preference rule?'))
            return;
        this.saving = true;
        this.error = '';
        this.api.removePreference(this.selected.contact_relationship_uuid, row.carrier_preference_uuid, row.version)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: detail => { this.saving = false; this.adopt(detail); this.notice = 'Preference removed.'; this.load(); }, error: error => this.failed(error)
        });
    }
    roleLabel(role: string): string { return this.catalogue?.movement_roles.find(item => item.movement_role === role)?.label || role; }
    selectTab(tab: 'relationships' | 'routing'): void {
        if (tab === this.activeTab || this.saving || this.routingStops?.saving)
            return;
        this.activeTab = tab;
    }
    clearCustomerFilter(): void {
        this.customerFilter = '';
        this.load(0);
    }
    canDeactivate(): boolean {
        return !this.saving && !this.routingStops?.saving && this.confirmDiscard()
            && (this.routingStops?.confirmDiscard() ?? true);
    }
    @HostListener('window:beforeunload', ['$event'])
    beforeUnload(event: BeforeUnloadEvent): void {
        if (this.relationshipDirty || this.preferenceDirty || this.routingStops?.dirty || this.saving || this.routingStops?.saving) {
            event.preventDefault();
            event.returnValue = '';
        }
    }
}
